import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface WhatsAppPhoneCardData {
  id: string;
  displayPhoneNumber: string;
  whatsapp_phone_number?: string;
  whatsapp_phone_number_id?: string;
  whatsapp_business_account_id?: string;
  status?: string;
  isVerified?: boolean;
  verifiedBy?: string;
  verifiedName?: string;
  qualityRating?: string;
  qualityFlagged?: boolean;
  currentMessagingLimit?: string | number;
  registered_at?: string;
  is_default?: boolean;
  codeVerificationStatus?: string;
}

export const QUALITY_COLORS: Record<string, string> = {
  GREEN: '#25D366',
  YELLOW: '#F5A623',
  RED: '#E74C3C',
  UNKNOWN: '#9CA3AF',
};

interface WhatsAppPhoneCardProps {
  phone: WhatsAppPhoneCardData;
  onPress?: (phone: WhatsAppPhoneCardData) => void;
  onVerify?: (phone: WhatsAppPhoneCardData) => void;
  onSetDefault?: (phoneId: string) => void;
  onDelete?: (phoneId: string) => void;
  /** Registers an already-connected (e.g. via embedded signup) number that's still
   * "Pending" on the Cloud API — distinct from onVerify, which is the SMS/voice OTP
   * flow for a number added under our shared WABA. */
  onRegister?: (phone: WhatsAppPhoneCardData) => void;
  isRegistering?: boolean;
  showActions?: boolean;
}

export const WhatsAppPhoneCard: React.FC<WhatsAppPhoneCardProps> = ({
  phone,
  onPress,
  onVerify,
  onSetDefault,
  onDelete,
  onRegister,
  isRegistering = false,
  showActions = true,
}) => {
  const styles = useThemedStyles(getStyles);
  const isPhoneVerified = phone.isVerified || 
                         phone.status === 'verified' || 
                         phone.status === 'VERIFIED' ||
                         phone.codeVerificationStatus === 'VERIFIED';
  
  const isMetaVerified = phone.verifiedBy === 'meta_admin';
  const displayNumber = phone.displayPhoneNumber || phone.whatsapp_phone_number || 'Unknown';
  const statusText = isPhoneVerified 
    ? (isMetaVerified ? 'Verified by Meta' : 'Verified') 
    : 'Pending Verification';

  return (
    <TouchableOpacity
      style={[
        styles.card, 
        isPhoneVerified && styles.verifiedCard,
        phone.is_default && styles.defaultCard
      ]}
      onPress={() => onPress?.(phone)}
      activeOpacity={0.7}
    >
      <View style={styles.cardContent}>
        <View style={styles.phoneInfo}>
          <View style={styles.phoneHeader}>
            <View style={[
              styles.phoneIconWrapper,
              isPhoneVerified && styles.verifiedIconWrapper
            ]}>
              <Ionicons 
                name={isPhoneVerified ? "checkmark-circle" : "time-outline"} 
                size={20} 
                color={isPhoneVerified ? colors.success : colors.warning} 
              />
            </View>
            <View style={styles.phoneDetails}>
              <View style={styles.phoneNumberRow}>
                <Text style={[styles.phoneNumber, isPhoneVerified && styles.verifiedText]}>
                  {displayNumber}
                </Text>
                {phone.is_default && (
                  <View style={styles.defaultBadge}>
                    <Text style={styles.defaultBadgeText}>Default</Text>
                  </View>
                )}
              </View>
              <View style={styles.statusRow}>
                <Text style={[
                  styles.statusText,
                  isPhoneVerified ? styles.verifiedStatusText : styles.pendingStatusText
                ]}>
                  {statusText}
                </Text>
                {phone.verifiedName && (
                  <Text style={styles.verifiedName}>
                    as "{phone.verifiedName}"
                  </Text>
                )}
              </View>
              {phone.qualityRating && phone.qualityRating !== 'UNKNOWN' && (
                <View style={styles.qualityRow}>
                  <View style={[styles.qualityDot, { backgroundColor: QUALITY_COLORS[phone.qualityRating] || colors.text.secondary }]} />
                  <Text style={styles.qualityRating}>
                    Quality: {phone.qualityRating}
                    {phone.currentMessagingLimit ? ` · Limit: ${phone.currentMessagingLimit}` : ''}
                  </Text>
                </View>
              )}
              {phone.qualityFlagged && (
                <View style={styles.flaggedBadge}>
                  <Ionicons name="warning" size={12} color={colors.error} />
                  <Text style={styles.flaggedBadgeText}>Flagged by Meta for quality</Text>
                </View>
              )}
              {phone.status && phone.status !== 'verified' && phone.status !== 'VERIFIED' && (
                <Text style={styles.statusDetail}>
                  Status: {phone.status}
                </Text>
              )}
            </View>
          </View>
        </View>

        {showActions && (
          <View style={styles.actions}>
            {!isPhoneVerified && phone.verifiedBy === 'embedded_signup' && onRegister && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onRegister(phone);
                }}
                activeOpacity={0.7}
                disabled={isRegistering}
              >
                {isRegistering ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons name="cloud-upload-outline" size={18} color={colors.primary} />
                )}
              </TouchableOpacity>
            )}
            {!isPhoneVerified && phone.verifiedBy !== 'embedded_signup' && onVerify && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onVerify(phone);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="shield-checkmark-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
            )}
            {!phone.is_default && isPhoneVerified && onSetDefault && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onSetDefault(phone.id);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="star-outline" size={18} color={colors.text.secondary} />
              </TouchableOpacity>
            )}
            {onDelete && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onDelete(phone.id);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={18} color={colors.error} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    }),
  },
  verifiedCard: {
    borderColor: colors.success,
    backgroundColor: withAlpha(colors.success, 0.03),
  },
  defaultCard: {
    borderColor: colors.primary,
    borderWidth: 2,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  phoneInfo: {
    flex: 1,
  },
  phoneHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  phoneIconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  verifiedIconWrapper: {
    backgroundColor: withAlpha(colors.success, 0.13),
  },
  phoneDetails: {
    flex: 1,
  },
  phoneNumberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  phoneNumber: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  verifiedText: {
    color: colors.success,
  },
  defaultBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.full,
    backgroundColor: colors.primary,
  },
  defaultBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs / 2,
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  verifiedStatusText: {
    color: colors.success,
  },
  pendingStatusText: {
    color: colors.warning,
  },
  verifiedName: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontStyle: 'italic',
  },
  qualityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    marginTop: spacing.xs / 2,
  },
  qualityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  qualityRating: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  flaggedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    marginTop: spacing.xs / 2,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: withAlpha(colors.error, 0.08),
    alignSelf: 'flex-start',
  },
  flaggedBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.medium,
  },
  statusDetail: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: spacing.xs / 2,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
  },
});
